/**
 * Whether a return leg earns the provider's round-trip fare.
 *
 * The provider only grants that fare when it sells both legs, and the seat lock charges accordingly.
 * Quoting it anywhere else advertises a discount the purchase will not honor, so every surface that
 * chooses between `roundTripPricing` and `pricing` has to ask the same question. It lives here
 * instead of being re-derived per component: the bug this fixes was the same rule written in several
 * places, and only some of them checking it.
 */

/**
 * Reduces a leg to the endpoints and provider used to compare it against the other leg.
 *
 * A leg can span several fragments (a round trip with connections). The pair is decided once for the
 * whole leg — the first fragment's origin and the last fragment's destination — so every fragment of
 * a leg gets the same fare and a connection cannot end up mixing the single and round-trip fares.
 * @param {Array<Object>} fragments - Purchase fragments for one leg, in travel order.
 * @returns {Object|null} Leg endpoints, or null when the fragments carry no trip.
 */
export function legFromFragments(fragments) {
  const list = fragments || [];
  const trips = list.map((fragment) => fragment?.trip);
  const first = trips[0];
  const last = trips[trips.length - 1];
  if (!first || !last || trips.some((trip) => !trip)) return null;

  const providerOf = (trip) => trip.lineId ?? trip.line?.id;
  // Every fragment of the leg has to be the same provider and the same transport. A connection
  // sold partly by another provider is not a leg any single provider covers end to end, so it
  // cannot earn a round-trip fare — reading only the first fragment would let it pass as one.
  const sameProvider = trips.every((trip) => providerOf(trip) === providerOf(first));
  const sameTransport = trips.every((trip) => trip.transportType === first.transportType);

  return {
    transportType: sameTransport ? first.transportType : 'mixed',
    lineId: sameProvider ? providerOf(first) : undefined,
    mixedProviders: !sameProvider,
    originId: first.originId,
    destinationId: last.destinationId,
  };
}

/**
 * Whether the return leg is entitled to the provider's round-trip fare.
 *
 * Unknown input answers `true`: a caller that cannot see the other leg keeps today's behavior rather
 * than silently dropping a discount the purchase may well grant.
 * @param {Object} params - Helper params.
 * @param {Object} [params.departureLeg] - Departure leg, or a parsed departure trip.
 * @param {Object} [params.returnLeg] - Return leg, or a parsed return trip.
 * @returns {boolean} True when the round-trip fare applies.
 */
export function roundTripFareApplies({ departureLeg, returnLeg }) {
  if (!departureLeg || !returnLeg) return true;

  // A leg split across providers has no single provider selling it end to end.
  if (departureLeg.mixedProviders || returnLeg.mixedProviders) return false;

  if (![departureLeg.transportType, returnLeg.transportType].every((type) => type === 'bus')) {
    return false;
  }

  // Purchase fragments expose the provider as `lineId`; parsed search trips also carry a `line`
  // object. Reading both is what makes this comparison effective on the purchase side, where
  // `line` is absent and a `line.id` check would silently compare undefined against undefined.
  const departureLine = departureLeg.lineId ?? departureLeg.line?.id;
  const returnLine = returnLeg.lineId ?? returnLeg.line?.id;
  if (departureLine && returnLine && departureLine !== returnLine) return false;

  return (
    departureLeg.originId === returnLeg.destinationId &&
    departureLeg.destinationId === returnLeg.originId
  );
}

/**
 * Whether a purchase's return leg earns the round-trip fare, as every pre-checkout surface decides
 * it: always when the tenant does not validate the round-trip discount, otherwise only when one
 * provider sells both legs.
 * @param {Object} params - Helper params.
 * @param {boolean} [params.validateRoundTripDiscount] - The tenant's `VALIDATE_ROUND_TRIP_DISCOUNT`.
 * @param {Array<Object>} [params.departFragments] - The depart leg's purchase fragments.
 * @param {Array<Object>} [params.returnFragments] - The return leg's purchase fragments.
 * @returns {boolean} True when the return leg is priced with `roundTripPricing`.
 */
export function returnLegEarnsRoundTripFare({
  validateRoundTripDiscount,
  departFragments,
  returnFragments,
}) {
  return (
    !validateRoundTripDiscount ||
    roundTripFareApplies({
      departureLeg: legFromFragments(departFragments),
      returnLeg: legFromFragments(returnFragments),
    })
  );
}

/**
 * The pricing object a purchase leg renders before checkout: the depart leg's
 * `departureRoundTripPricing` on a round trip and `pricing` one way; the return leg's
 * `roundTripPricing` when it earns the round-trip fare, otherwise `pricing`. The sidebar and the
 * final-price arm gate both read it here, so the gate always reads the pricing that is painted.
 * @param {Object} params - Helper params.
 * @param {string} params.way - `departs` or `returns`.
 * @param {boolean} [params.isRoundTrip] - Whether the purchase is a round trip.
 * @param {boolean} [params.earnsRoundTripFare] - Whether the return leg earns the round-trip fare
 *   (`returnLegEarnsRoundTripFare`).
 * @returns {string} The pricing key on the leg's fragments.
 */
export function purchaseLegPricingKey({ way, isRoundTrip, earnsRoundTripFare }) {
  if (way === 'returns') return earnsRoundTripFare ? 'roundTripPricing' : 'pricing';
  return isRoundTrip ? 'departureRoundTripPricing' : 'pricing';
}

export default roundTripFareApplies;
